import { useState, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { useIntl } from 'react-intl'
import { useQueryClient } from '@tanstack/react-query'
import { useFormatMessage } from '@/translations/useFormatMessage'
import { formatPrice } from '@/lib/formatPrice'
import { QUERY_STALE_TIMES } from '@/lib/queryConfig'
import { PATHS } from '@/router/paths'
import { useLocationRef } from '@/contexts/RefContext'
import { useAppDispatch, useAppSelector } from '@/store'
import {
	addItem,
	calculate,
	selectSelectedOrderType,
	selectDineInTableName,
} from '@/store/ticketSlice'
import { getMenuItemDetail } from '@/shared/api/menu'
import type { MenuItem as MenuItemData } from '@/shared/api/menu'
import {
	AddButton,
	CardBody,
	CardDescription,
	CardImage,
	CardImagePlaceholder,
	CardImageWrap,
	CardInfo,
	CardName,
	CardPrice,
	CardPriceRow,
	CardRoot,
	SoldOutLabel,
	StockBadge,
} from './MenuItem.styled'

interface MenuItemProps {
	item: MenuItemData
	currency?: string
}

export default function MenuItem({ item, currency = 'USD' }: MenuItemProps) {
	const { t } = useFormatMessage()
	const intl = useIntl()
	const navigate = useNavigate()
	const locationRef = useLocationRef()
	const dispatch = useAppDispatch()
	const queryClient = useQueryClient()
	const selectedOrderType = useAppSelector(selectSelectedOrderType)
	const dineInTableName = useAppSelector(selectDineInTableName)
	const isDineIn = !!dineInTableName

	const isOutOfStock = item.stockStatus === 'OUT_OF_STOCK'
	const isLowStock = item.stockStatus === 'LOW_STOCK'
	const allowSpecialRequest = selectedOrderType?.allowSpecialRequest ?? false

	// useRef provides a synchronous guard — two rapid clicks in the same render frame
	// would both read isAdding state as false before the re-render, so React state alone
	// is not sufficient. The ref is set synchronously on the first click.
	const isAddingRef = useRef(false)
	const [isAdding, setIsAdding] = useState(false)

	const price = formatPrice(item.onlinePrice ?? item.price, intl.locale, currency)

	async function handleItemClick(e: React.SyntheticEvent) {
		e.stopPropagation()
		if (isAddingRef.current || isOutOfStock) return
		isAddingRef.current = true
		setIsAdding(true)
		try {
			const detail = await queryClient.fetchQuery({
				queryKey: ['menu-item-detail', locationRef, item.ref, isDineIn],
				queryFn: () => getMenuItemDetail(locationRef ?? '', item.ref, isDineIn),
				staleTime: QUERY_STALE_TIMES.REALTIME,
			})
			if (detail.modifierSets.length === 0 && !allowSpecialRequest) {
				dispatch(
					addItem({
						locationRef: locationRef ?? '',
						itemRef: detail.ref,
						quantity: 1,
						specialRequest: null,
						modifiers: [],
					}),
				)
				void dispatch(calculate())
			} else {
				void navigate(PATHS.addItem(locationRef, item.ref))
			}
		} catch {
			void navigate(PATHS.addItem(locationRef, item.ref))
		} finally {
			isAddingRef.current = false
			setIsAdding(false)
		}
	}

	return (
		<CardRoot
			isOutOfStock={isOutOfStock}
			aria-disabled={isOutOfStock || undefined}
			data-testid="menu-item"
			role={isOutOfStock ? undefined : 'button'}
			tabIndex={isOutOfStock ? -1 : 0}
			onClick={isOutOfStock ? undefined : (e) => void handleItemClick(e)}
			onKeyDown={
				isOutOfStock
					? undefined
					: (e) => {
							if (e.key === 'Enter' || e.key === ' ') {
								e.preventDefault()
								void handleItemClick(e)
							}
						}
			}
		>
			<CardBody>
				<CardInfo>
					<CardName isOutOfStock={isOutOfStock}>{item.name}</CardName>
					{item.description && (
						<CardDescription isOutOfStock={isOutOfStock}>
							{item.description}
						</CardDescription>
					)}
				</CardInfo>

				<CardPriceRow>
					<CardPrice>{price}</CardPrice>
					{isOutOfStock && (
						<SoldOutLabel>{t('component:menuItem:soldOut:label')}</SoldOutLabel>
					)}
				</CardPriceRow>
			</CardBody>

			<CardImageWrap>
				{item.imageUrl800x800 ? (
					<CardImage
						src={item.imageUrl800x800}
						alt=""
						loading="lazy"
						width={144}
						height={144}
					/>
				) : (
					<CardImagePlaceholder aria-hidden="true" />
				)}

				{isLowStock && <StockBadge>{t('component:menuItem:lowStock:badge')}</StockBadge>}

				<AddButton
					isOutOfStock={isOutOfStock}
					aria-label={t('component:menuItem:addItem:label', { name: item.name })}
					aria-hidden={isOutOfStock || undefined}
					tabIndex={-1}
					disabled={isAdding}
					onClick={(e) => void handleItemClick(e)}
				>
					+
				</AddButton>
			</CardImageWrap>
		</CardRoot>
	)
}
